import PropTypes from 'prop-types';
import { Box, Skeleton, Typography } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import useAccountInteractions from 'features/IRWorkstation/hooks/useAccountInteractions';
import { formatDate } from 'utils/utils';

const SCROLL_END_THRESHOLD = 100;

const InteractionRow = ({ interaction }) => {
	const subLabel = [interaction.owner, interaction.type]
		.filter(Boolean)
		.join(' · ');

	return (
		<Box
			sx={{
				padding: '10px 15px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '12px',
				}}
			>
				<Typography
					sx={{
						fontSize: '12px',
						fontWeight: 700,
						color: 'text.primary',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{interaction.name}
				</Typography>
				<Typography
					sx={{ fontSize: '12px', color: 'text.secondary', flexShrink: 0 }}
				>
					{formatDate(interaction.date, true)}
				</Typography>
			</Box>
			{subLabel && (
				<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
					{subLabel}
				</Typography>
			)}
		</Box>
	);
};

InteractionRow.propTypes = {
	interaction: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
		owner: PropTypes.string,
		date: PropTypes.string,
		type: PropTypes.string,
	}).isRequired,
};

const InteractionRowSkeleton = ({ dataTestId }) => (
	<Box
		data-testid={dataTestId}
		sx={{
			padding: '10px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: '12px',
			}}
		>
			<Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
			<Skeleton
				variant='text'
				width={70}
				sx={{ fontSize: '12px', flexShrink: 0 }}
			/>
		</Box>
		<Skeleton variant='text' width='35%' sx={{ fontSize: '11px' }} />
	</Box>
);

InteractionRowSkeleton.propTypes = {
	dataTestId: PropTypes.string.isRequired,
};

const CenteredMessage = ({ children, color = 'text.secondary' }) => (
	<Box
		sx={{
			minHeight: 200,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}
	>
		<Typography color={color} sx={{ fontSize: '12px' }}>
			{children}
		</Typography>
	</Box>
);

CenteredMessage.propTypes = {
	children: PropTypes.node.isRequired,
	color: PropTypes.string,
};

const LPRecentInteractionsCard = ({ accountId }) => {
	const {
		interactions,
		loading,
		loadingMore,
		error,
		loadMore,
	} = useAccountInteractions(accountId);

	const handleScroll = (event) => {
		const { scrollTop, clientHeight, scrollHeight } = event.target;
		if (scrollHeight - scrollTop - clientHeight <= SCROLL_END_THRESHOLD) {
			loadMore();
		}
	};

	return (
		<Box
			data-testid='lp-recent-interactions-card'
			sx={{
				width: '100%',
				height: '100%',
				minWidth: 0,
				minHeight: 0,
				display: 'flex',
				'& > *': { flex: 1 },
			}}
		>
			<QuadPanel title='Recent interactions' onScroll={handleScroll}>
				{error ? (
					<CenteredMessage color='error'>
						Unable to load Interaction records
					</CenteredMessage>
				) : loading ? (
					<Box>
						{Array.from({ length: 4 }, (_, index) => (
							<InteractionRowSkeleton
								key={index}
								dataTestId='lp-interaction-row-skeleton'
							/>
						))}
					</Box>
				) : interactions.length === 0 ? (
					<CenteredMessage>No interactions on file</CenteredMessage>
				) : (
					<Box>
						{interactions.map((interaction) => (
							<InteractionRow key={interaction.id} interaction={interaction} />
						))}
						{loadingMore && (
							<InteractionRowSkeleton dataTestId='lp-interaction-loading-more-skeleton' />
						)}
					</Box>
				)}
			</QuadPanel>
		</Box>
	);
};

LPRecentInteractionsCard.propTypes = {
	accountId: PropTypes.string.isRequired,
};

export default LPRecentInteractionsCard;
